import { useEffect, useRef, useState, useCallback } from "react";
import { api, errText, media } from "../../lib/api";
import { fmtDate } from "../../components/DateField";
import { Upload, FileText, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

const STATUS_COPY = {
  pending_review: { label: "En attente de validation", tone: "text-warning border-warning/40 bg-warning/5" },
  approved: { label: "Validée · dans la file de virement", tone: "text-success border-success/40 bg-success/5" },
  rejected: { label: "À corriger", tone: "text-danger border-danger/40 bg-danger/5" },
  processed: { label: "Payée", tone: "text-ink border-ink/30 bg-plaster-deep" },
};

export default function Payouts() {
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        api.get("/florist/payout-requests/balance"),
        api.get("/florist/payout-requests"),
      ]);
      setBalance(b.data);
      setRequests(r.data);
    } catch (e) { setMsg(errText(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("invoice", file);
      fd.append("notes", notes);
      await api.post("/florist/payout-requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Demande envoyée. Fleurinelle vous répond sous 48h.");
      setNotes("");
      await load();
    } catch (e) { setMsg(errText(e)); }
    finally { setUploading(false); event.target.value = ""; }
  };

  const reupload = async (id, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("invoice", file);
      await api.post(`/florist/payout-requests/${id}/reupload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Nouvelle facture envoyée.");
      await load();
    } catch (e) { setMsg(errText(e)); }
    finally { setUploading(false); event.target.value = ""; }
  };

  const openStatement = async (id) => {
    const r = await api.get(`/florist/payout-requests/${id}/statement`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "text/html" })), "_blank");
  };

  if (!balance) return <p className="text-sm text-muted py-8" data-testid="payouts-loading">{msg || "Chargement…"}</p>;

  const canRequest = balance.available_mad > 0 && !requests.some((r) => r.status === "pending_review");
  const openPending = requests.find((r) => r.status === "pending_review");
  const openRejected = requests.find((r) => r.status === "rejected");

  return (
    <div className="space-y-4" data-testid="florist-payouts-tab">
      {msg ? <p className="text-xs text-brass" data-testid="payouts-message">{msg}</p> : null}

      <div className="card p-4 space-y-2">
        <p className="smallcaps">Solde disponible</p>
        <p className="font-display text-4xl text-ink tabular" data-testid="payouts-available">{balance.available_mad} MAD</p>
        <p className="text-xs text-muted">
          {balance.orders_count} commande{balance.orders_count === 1 ? "" : "s"} livrée{balance.orders_count === 1 ? "" : "s"} et hors délai de rétractation ({balance.refund_window_days} jour{balance.refund_window_days === 1 ? "" : "s"}).
        </p>
        {balance.hold_mad ? (
          <p className="text-xs text-warning" data-testid="payouts-hold">
            {balance.hold_mad} MAD encore en délai de rétractation ({balance.hold_orders} commandes) — disponibles dans quelques jours.
          </p>
        ) : null}
        {balance.in_review_mad ? (
          <p className="text-xs text-muted" data-testid="payouts-in-review">
            {balance.in_review_mad} MAD déjà en cours de validation.
          </p>
        ) : null}
        <p className="text-xs text-muted">
          Virement vers {balance.bank?.bank_name || "banque non renseignée"} · {balance.bank?.account_holder || "—"} · RIB ····{balance.bank?.rib_last4 || "----"}
        </p>
      </div>

      {openRejected ? (
        <div className={`card p-4 space-y-2 ${STATUS_COPY.rejected.tone}`} data-testid="payouts-rejected-card">
          <p className="text-sm flex items-center gap-2"><AlertTriangle size={14} /> Votre facture a été refusée.</p>
          <p className="text-xs"><b>Motif Fleurinelle :</b> {openRejected.rejection_reason}</p>
          <p className="text-xs text-muted">Corrigez la facture, puis re-téléversez-la ci-dessous.</p>
          <label className="btn-primary text-xs inline-flex items-center gap-2 cursor-pointer w-fit">
            <RefreshCw size={12} /> {uploading ? "Envoi…" : "Envoyer une facture corrigée"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`payouts-reupload-${openRejected.id}`}
                   onChange={(e) => reupload(openRejected.id, e)} disabled={uploading} />
          </label>
        </div>
      ) : null}

      {!openRejected && canRequest ? (
        <div className="card p-4 space-y-3" data-testid="payouts-request-card">
          <p className="smallcaps">Demander le versement</p>
          <p className="text-xs text-muted">
            1. Émettez une facture officielle et signée pour le montant ci-dessus. 2. Téléversez-la (PDF, JPG ou PNG). 3. Fleurinelle valide sous 48h.
          </p>
          <textarea className="field" placeholder="Note à l'équipe Fleurinelle (facultatif)"
                    data-testid="payouts-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                 data-testid="payouts-invoice-input" onChange={submit} disabled={uploading} />
          <button className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="payouts-request-btn"
                  disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Upload size={14} /> {uploading ? "Envoi de la facture…" : `Téléverser ma facture · ${balance.available_mad} MAD`}
          </button>
        </div>
      ) : null}

      {openPending && !openRejected ? (
        <div className={`card p-4 ${STATUS_COPY.pending_review.tone}`} data-testid="payouts-pending-card">
          <p className="text-sm">Votre facture pour {openPending.amount_mad} MAD est en cours de validation par Fleurinelle.</p>
          <p className="text-xs mt-1">Vous serez notifié dès qu'elle est validée ou si une correction est nécessaire.</p>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="smallcaps">Historique des demandes</p>
        {requests.length === 0 ? <p className="text-sm text-muted">Aucune demande pour le moment.</p> : null}
        {requests.map((r) => {
          const st = STATUS_COPY[r.status] || { label: r.status, tone: "text-muted" };
          return (
            <article key={r.id} className={`card p-3 space-y-2 border ${st.tone}`} data-testid={`payouts-req-${r.id}`}>
              <div className="flex flex-wrap gap-2 items-baseline">
                <p className="text-sm text-ink tabular font-medium">{r.amount_mad} MAD</p>
                <p className="text-xs text-muted">· {r.orders} commande{r.orders === 1 ? "" : "s"} · {fmtDate(r.period_start)} → {fmtDate(r.period_end)}</p>
                <p className="text-xs ms-auto uppercase tracking-wider">{st.label}</p>
              </div>
              {r.rejection_reason ? <p className="text-xs text-danger"><b>Motif :</b> {r.rejection_reason}</p> : null}
              {r.transfer_reference ? <p className="text-xs">Référence de virement : <b>{r.transfer_reference}</b></p> : null}
              <div className="flex flex-wrap gap-2">
                {r.invoice_url ? (
                  <a className="btn-ghost text-[11px] inline-flex items-center gap-1" href={media(r.invoice_url)} target="_blank" rel="noreferrer" data-testid={`payouts-req-invoice-${r.id}`}>
                    <FileText size={12} /> Ma facture ({r.invoice_filename || "PDF"})
                  </a>
                ) : null}
                {["approved", "processed"].includes(r.status) ? (
                  <button className="btn-ghost text-[11px] inline-flex items-center gap-1" data-testid={`payouts-req-statement-${r.id}`}
                          onClick={() => openStatement(r.id)}>
                    <CheckCircle2 size={12} /> Relevé Fleurinelle
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
